/**
 * Grammar translation and cross-reference disambiguation utilities.
 *
 * Prevents dictionary entries for words like "bearing", "training", "winding", etc.
 * from being mistranslated into literal homograph nouns (e.g. "bear" -> "urso", "train" -> "trem", "wind" -> "vento")
 * when dictionary APIs return grammatical inflection stubs ("present participle and gerund of bear").
 */

// Common homographs where the verb root is frequently mistranslated as a noun or different meaning by machine translation
const HOMOGRAPH_VERB_MAP: Record<string, string> = {
  bear: "do verbo 'to bear' (suportar, aguentar, carregar, produzir, ter relação)",
  train: "do verbo 'to train' (treinar, capacitar, preparar)",
  wind: "do verbo 'to wind' (enrolar, serpentear, dar corda)",
  spring: "do verbo 'to spring' (saltar, brotar, surgir)",
  fall: "do verbo 'to fall' (cair, diminuir)",
  fly: "do verbo 'to fly' (voar, pilotar)",
  watch: "do verbo 'to watch' (assistir, vigiar, observar)",
  match: "do verbo 'to match' (combinar, corresponder, equiparar)",
  ring: "do verbo 'to ring' (tocar, soar, retinir)",
  light: "do verbo 'to light' (acender, iluminar)",
  sound: "do verbo 'to sound' (soar, emitir som, sondar)",
  mean: "do verbo 'to mean' (significar, ter intenção)",
  lead: "do verbo 'to lead' (liderar, conduzir, guiar)",
  ground: "do verbo 'to ground' (basear, aterrar) ou particípio de 'grind' (moer, triturar)",
  leave: "do verbo 'to leave' (partir, sair, deixar)",
  plant: "do verbo 'to plant' (plantar, semear)",
  book: "do verbo 'to book' (reservar, agendar)",
  park: "do verbo 'to park' (estacionar)",
  ship: "do verbo 'to ship' (enviar, transportar)",
  water: "do verbo 'to water' (regar, aguar)",
  tear: "do verbo 'to tear' (rasgar, despedaçar)",
  bow: "do verbo 'to bow' (inclinar-se, curvar-se)",
  row: "do verbo 'to row' (remar)"
};

/**
 * Checks whether a definition text is merely an inflectional cross-reference stub
 * (e.g., "present participle and gerund of bear", "plural of glass", "past participle of bind").
 */
export function isGrammaticalCrossReference(defText?: string | null): boolean {
  if (!defText || typeof defText !== 'string') return false;
  const clean = defText.trim().toLowerCase();

  return (
    /^(present participle(\s+and\s+gerund)?|gerund(\s+and\s+present participle)?|past participle|simple past(\s+and\s+past participle)?|plural|third-person singular)\s+of\b/i.test(clean) ||
    /^(inflection of|form of|alternative form of|archaic form of|variant of)\b/i.test(clean) ||
    /^(present participle|past tense|past participle)\s*:/i.test(clean)
  );
}

/**
 * Parses and translates grammatical cross-reference stubs into clean, accurate Portuguese.
 * Disambiguates verb homographs so "bear" is never translated as "urso", "train" as "trem", etc.
 */
export function translateGrammaticalCrossReference(defText?: string | null): string | null {
  if (!defText || typeof defText !== 'string') return null;
  const trimmed = defText.trim();

  // Pattern: (present participle and gerund | present participle | gerund | past participle | simple past | plural) of <word>
  const match = trimmed.match(
    /^(present participle\s+and\s+gerund|gerund\s+and\s+present participle|present participle|gerund|past participle|simple past\s+and\s+past participle|simple past|plural|third-person singular)\s+of\s+([a-zA-Z'\-]+)/i
  );

  if (!match) {
    // Check variant / alternative form of <word>
    const altMatch = trimmed.match(/^(?:alternative|archaic|variant)\s+form\s+of\s+([a-zA-Z'\-]+)/i);
    if (altMatch) {
      const base = altMatch[1].toLowerCase();
      return `Forma variante de '${base}'`;
    }
    return null;
  }

  const relation = match[1].toLowerCase();
  const rawBase = match[2].toLowerCase();

  let relationPt = 'Forma';
  if (relation.includes('present participle') && relation.includes('gerund')) {
    relationPt = 'Particípio presente e gerúndio';
  } else if (relation.includes('present participle')) {
    relationPt = 'Particípio presente';
  } else if (relation === 'gerund') {
    relationPt = 'Gerúndio';
  } else if (relation.includes('past participle') && relation.includes('simple past')) {
    relationPt = 'Passado simples e particípio passado';
  } else if (relation.includes('past participle')) {
    relationPt = 'Particípio passado';
  } else if (relation.includes('simple past')) {
    relationPt = 'Passado simples';
  } else if (relation.includes('plural')) {
    relationPt = 'Plural';
  } else if (relation.includes('third-person')) {
    relationPt = 'Terceira pessoa do singular do presente';
  }

  // Base word translation / explanation
  if (relation.includes('plural')) {
    return `${relationPt} do substantivo '${rawBase}'`;
  }

  if (HOMOGRAPH_VERB_MAP[rawBase]) {
    return `${relationPt} ${HOMOGRAPH_VERB_MAP[rawBase]}`;
  }

  return `${relationPt} do verbo 'to ${rawBase}'`;
}
